import { useState } from 'react';
import { Printer } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { formatDateTime, formatMoney } from '@/lib/format';
import type { AccountTransaction } from '@/api/endpoints';
import { Alert, Button, Modal } from '@/components/ui';
import { printDocument } from '@/lib/printing';
import { cashMovementDocument } from '@/lib/receiptDocuments';

const MOVEMENT_TITLES: Record<string, string> = {
  PAYMENT_IN: 'CASH RECEIVED',
  PAYMENT_OUT: 'CASH PAID OUT',
  EXPENSE: 'EXPENSE PAID',
  DEPOSIT: 'CASH DEPOSITED',
  WITHDRAWAL: 'CASH WITHDRAWN',
  ADJUSTMENT: 'CASH CORRECTION',
  OPENING: 'OPENING BALANCE',
  TRANSFER_IN: 'TRANSFER IN',
  TRANSFER_OUT: 'TRANSFER OUT',
};

/**
 * A printable slip for one cash movement.
 *
 * Every line in the ledger is something a person can be asked to account for —
 * money handed over, an expense settled, a drawer corrected — so every line can
 * be printed. Laid out for the same 58mm roll as the sale receipts.
 */
export function CashMovementReceipt({
  entry,
  onClose,
}: {
  entry: AccountTransaction;
  onClose: () => void;
}) {
  const format = useFormat();
  const context = useAppSelector((state) => state.tenant.context);
  const [printing, setPrinting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const print = async (): Promise<void> => {
    setFailure(null);
    setPrinting(true);
    try {
      const result = await printDocument(
        cashMovementDocument(entry, organization ?? {}, format, MOVEMENT_TITLES),
      );
      if (!result.printed) setFailure(result.error);
    } finally {
      setPrinting(false);
    }
  };

  const organization = context?.organization;
  const settings = context?.settings;

  const address = [organization?.addressLine1, organization?.addressLine2, organization?.city]
    .filter(Boolean)
    .join(', ');

  const title = MOVEMENT_TITLES[entry.type] ?? entry.type.replaceAll('_', ' ');
  const inbound = entry.direction === 'IN';

  const row = (label: string, value?: string | null) =>
    value ? (
      <div className="flex justify-between gap-2">
        <span>{label}</span>
        <span className="text-right">{value}</span>
      </div>
    ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Cash slip"
      description={`${entry.account.name} · ${formatDateTime(entry.occurredAt, format)}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button icon={Printer} loading={printing} onClick={() => void print()}>Print</Button>
        </div>
      }
    >
      {failure && <div className="mb-3"><Alert tone="danger">{failure}</Alert></div>}

      <div
        id="receipt-print-area"
        className="mx-auto max-w-[58mm] bg-white p-3 font-mono text-[10px] leading-snug text-black"
      >
        <div className="text-center">
          <p className="text-xs font-bold uppercase">{organization?.name}</p>
          {address && <p>{address}</p>}
          {organization?.phone && <p>{organization.phone}</p>}
        </div>

        <hr className="my-2 border-dashed border-black" />
        <p className="text-center font-bold">{title}</p>
        <hr className="my-2 border-dashed border-black" />

        {row('Account', entry.account.name)}
        {row('When', formatDateTime(entry.occurredAt, format))}
        {row('Source', entry.referenceType.toLowerCase().replaceAll('_', ' '))}
        {row('Ref', entry.referenceNumber)}

        <hr className="my-2 border-black" />
        <p className="text-center">{inbound ? 'RECEIVED' : 'PAID OUT'}</p>
        <p className="text-center text-base font-bold">{formatMoney(entry.amount, format)}</p>
        <hr className="my-2 border-black" />

        {/* What the drawer held before and after, so the slip can be checked
            against the next one without opening the system. */}
        {row('Balance before', formatMoney(entry.balanceBefore, format))}
        {row('Balance after', formatMoney(entry.balanceAfter, format))}

        {entry.notes && (
          <>
            <hr className="my-2 border-dashed border-black" />
            <p>{entry.notes}</p>
          </>
        )}

        <hr className="my-2 border-dashed border-black" />
        {row(
          'Recorded by',
          entry.createdBy ? `${entry.createdBy.firstName} ${entry.createdBy.lastName}` : 'the system',
        )}
        <p className="mt-2 text-center">{settings?.receiptFooter ?? 'Keep this slip.'}</p>
        <p className="text-center text-[9px]">Printed {new Date().toLocaleString()}</p>
      </div>
    </Modal>
  );
}
